/**
 * InventoryCatalogueMatchDrawer — reconcile uncertain lines to catalogue SKUs.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  formatCatalogueMatchStatusLabel,
  formatCatalogueSuggestionLabel,
} from "../utils/automatedPreparationReviewGuidance.js";

const InventoryCatalogueMatchDrawer = ({
  open = false,
  item = null,
  quoteId = null,
  matching = false,
  onClose = null,
  onMatchCatalogue = null,
}) => {
  const [pendingSkuId, setPendingSkuId] = useState(null);
  const match = item?.catalogueMatch;
  const suggestions = match?.suggestions || [];
  const lineKey = item?.sourceItemId || item?.key;
  const canMatch = Boolean(
    quoteId && lineKey && typeof onMatchCatalogue === "function"
  );
  const isUnique = match?.matchStatus === "unique";

  const handleUseSku = async (suggestion) => {
    if (!canMatch || !suggestion?.skuId) return;
    setPendingSkuId(suggestion.skuId);
    try {
      await onMatchCatalogue({
        lineKey,
        catalogueSkuId: suggestion.skuId,
        suggestion,
      });
    } finally {
      setPendingSkuId(null);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      data-testid="inventory-catalogue-match-drawer"
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100vw",
          zIndex: (theme) => theme.zIndex.drawer + 2,
        },
      }}
    >
      <Box sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box pr={1}>
            <Typography variant="overline" color="text.secondary">
              Catalogue match
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {item?.description || "Unnamed item"}
            </Typography>
            {item?.quantity != null && (
              <Typography variant="caption" color="text.secondary">
                Quantity {item.quantity}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="Close catalogue match drawer"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <Chip
          size="small"
          variant="outlined"
          color={
            match?.matchStatus === "unique"
              ? "success"
              : match?.matchStatus === "ambiguous"
                ? "warning"
                : "default"
          }
          label={formatCatalogueMatchStatusLabel(match)}
          sx={{ alignSelf: "flex-start", mb: 1.5 }}
        />

        {match?.hint && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {match.hint}
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} mb={1}>
          {isUnique ? "Matched catalogue SKU" : "Pick a catalogue match"}
        </Typography>

        {!isUnique && canMatch && (
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Choose the catalogue row that best fits this inventory line. Pricing
            will refresh automatically.
          </Typography>
        )}

        {suggestions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No catalogue suggestions available for this item.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ flex: 1, overflow: "auto" }}>
            {suggestions.map((suggestion) => {
              const label =
                suggestion.suggestedLeadLabel ||
                suggestion.displayName ||
                suggestion.skuId;
              const isPending = pendingSkuId === suggestion.skuId;
              const isCurrentMatch =
                isUnique && match?.matchedSku?.skuId === suggestion.skuId;

              return (
                <ListItem
                  key={suggestion.skuId}
                  alignItems="flex-start"
                  sx={{
                    px: 0,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                  data-testid={`catalogue-suggestion-${suggestion.skuId}`}
                >
                  <ListItemText
                    primary={label}
                    secondary={
                      <>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatCatalogueSuggestionLabel(suggestion)}
                        </Typography>
                        {suggestion.skuId && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            SKU {suggestion.skuId}
                          </Typography>
                        )}
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  {canMatch && !isUnique ? (
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={matching || isPending}
                        onClick={() => handleUseSku(suggestion)}
                        startIcon={
                          isPending ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <CheckCircleOutlineIcon fontSize="small" />
                          )
                        }
                      >
                        Use this SKU
                      </Button>
                    </ListItemSecondaryAction>
                  ) : null}
                  {isCurrentMatch ? (
                    <Chip size="small" color="success" label="Current match" />
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        )}

        <Box mt={2} pt={2} borderTop={1} borderColor="divider">
          <Typography variant="caption" color="text.secondary">
            {canMatch && !isUnique
              ? "Matching stores the SKU on this quote preparation and clears the catalogue blocker for this line."
              : "Update the inventory line on the lead using an exact catalogue name, then return here and Re-run pricing."}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InventoryCatalogueMatchDrawer;
